import {ComponentType} from 'react';
import {NodeProps, EdgeProps} from 'reactflow';

import {
  ProcessExecutionType,
  ProcessFlowchartPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import TaskSchemaControlFlowEdge from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/edges/TaskSchemaControlFlowEdge';
import TaskSchemaDataFlowEdge from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/edges/TaskSchemaDataFlowEdge';
import {NodeFieldDetailsTab} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/DefaultTaskSchemaNodeDetailsSidepanelContent';
import FlowchartSettingsPane from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/DefaultTaskSchemaNodeFlowchartSettingsPane';
import PlaceholderNodeRenderer from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/PlaceholderNodeRenderer';
import RouterNodeRenderer from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/RouterNodeRenderer';
import StopNodeRenderer from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/StopNodeRenderer';
import TaskSchemaNodeRenderer from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {
  ProcessFlowchartNodeType,
  UserFacingNodeTypes,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeSection,
  NodeSettingsTabKeys,
  SidepanelTab,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import LogicNodeSidepanelSection from './left-sidepanel/LogicNodeSidepanelSection';
import {NodeSidepanelSectionProps} from './left-sidepanel/NodeSidepanelSection';
import TaskNodeSidepanelSection from './left-sidepanel/TaskNodeSidepanelSection';
import {getLogicNodeMetadata} from './logicNodeMetadata';

export const PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_EXECUTION_TYPE: Record<
  ProcessExecutionType,
  UserFacingNodeTypes | null
> = {
  [ProcessExecutionType.DIRECT]: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
  [ProcessExecutionType.ENTRY]: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
  [ProcessExecutionType.FLOWCHART]: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
  [ProcessExecutionType.PROCEDURE]: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
  [ProcessExecutionType.PROCEDURE_METHOD]: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
  [ProcessExecutionType.PROCEDURE_STEP]: null,
  [ProcessExecutionType.PIPELINE]: null,
};

export const PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_PLATFORM_FEATURE: Partial<
  Record<ProcessFlowchartPlatformFeatureType, UserFacingNodeTypes | null>
> = {
  [ProcessFlowchartPlatformFeatureType.ALLOW_ROUTERS]: ProcessFlowchartNodeType.ROUTER_NODE,
  [ProcessFlowchartPlatformFeatureType.ALLOW_STOP_NODES]: ProcessFlowchartNodeType.STOP_NODE,
};

export const PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES: Record<
  ProcessFlowchartNodeType,
  ComponentType<NodeProps>
> = {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: TaskSchemaNodeRenderer,
  [ProcessFlowchartNodeType.ROUTER_NODE]: RouterNodeRenderer,
  [ProcessFlowchartNodeType.STOP_NODE]: StopNodeRenderer,
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: PlaceholderNodeRenderer,
} as const;

export const PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE: Partial<
  Record<ProcessFlowchartPlatformFeatureType, SidepanelTab>
> = {
  [ProcessFlowchartPlatformFeatureType.ALLOW_CONTROL_FLOW_TOKEN_CONFIGURATION]: {
    key: NodeSettingsTabKeys.FLOWCHART_SETTINGS,
    label: 'Transitions',
    content: FlowchartSettingsPane,
  },
  [ProcessFlowchartPlatformFeatureType.ALLOW_FIELD_CONFIGURATION]: {
    key: NodeSettingsTabKeys.NODE_FIELD_DETAILS,
    label: 'Inputs & Outputs',
    content: NodeFieldDetailsTab,
    openOnFieldSelect: true,
  },
} as const;

export const PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES: Record<
  ProcessFlowchartEdgeType,
  ComponentType<EdgeProps>
> = {
  [ProcessFlowchartEdgeType.CONTROL_FLOW]: TaskSchemaControlFlowEdge,
  [ProcessFlowchartEdgeType.DATA_FLOW]: TaskSchemaDataFlowEdge,
};

export const NODE_SECTION_TO_RENDERER: Record<
  ProcessFlowchartNodeSection,
  (props: NodeSidepanelSectionProps) => JSX.Element | null
> = {
  [ProcessFlowchartNodeSection.LOGIC]: LogicNodeSidepanelSection,
  [ProcessFlowchartNodeSection.TASK_SCHEMAS]: TaskNodeSidepanelSection,
  [ProcessFlowchartNodeSection.PIPELINES]: () => null,
};

export const NODE_TYPE_TO_RENDERER: Record<ProcessFlowchartNodeType, (props: NodeProps) => JSX.Element> = {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: TaskSchemaNodeRenderer,
  [ProcessFlowchartNodeType.ROUTER_NODE]: RouterNodeRenderer,
  [ProcessFlowchartNodeType.STOP_NODE]: StopNodeRenderer,
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: PlaceholderNodeRenderer,
};
export const NODE_TYPE_TO_SECTION: Record<UserFacingNodeTypes, ProcessFlowchartNodeSection> = {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: ProcessFlowchartNodeSection.TASK_SCHEMAS,
  [ProcessFlowchartNodeType.ROUTER_NODE]: ProcessFlowchartNodeSection.LOGIC,
  [ProcessFlowchartNodeType.STOP_NODE]: ProcessFlowchartNodeSection.LOGIC,
};

export const NODE_TYPE_TO_NAME: Record<ProcessFlowchartNodeType, string> = {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: 'Task',
  [ProcessFlowchartNodeType.ROUTER_NODE]: getLogicNodeMetadata(ProcessFlowchartNodeType.ROUTER_NODE).name,
  [ProcessFlowchartNodeType.STOP_NODE]: getLogicNodeMetadata(ProcessFlowchartNodeType.STOP_NODE).name,
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: 'Placeholder',
};
